import _, { parseInt } from 'lodash'
import { adminClient as apolloClient } from '@/store/apollo';

import CmsGql from '@/store/graphql/cms/queries/Cms.gql';
import CMSsGql from '@/store/graphql/cms/queries/CMSs.gql';

import AttachCmsToShopGql from '@/store/graphql/cms/mutations/AttachCmsToShop.gql';
import AttachableShopsForCmsGql from '@/store/graphql/cms/queries/AttachableShopsForCms.gql';
import ClearCacheForCmsGql from '@/store/graphql/cms/mutations/ClearCacheForCms.gql';
import CopyCmsSettingsGql from '@/store/graphql/cms/mutations/CopyCmsSettings.gql';
import ProcessCmsPaginationBulkActionGql from '@/store/graphql/cms/mutations/ProcessCmsPaginationBulkAction.gql';

import UpdateCms from '@/store/graphql/cms/mutations/UpdateCms.gql';
import CreateCms from '@/store/graphql/cms/mutations/CreateCms.gql';
import CmssPaginated from '@/store/graphql/cms/queries/CmssPaginated.gql';
import DeleteCms from '@/store/graphql/cms/mutations/DeleteCms.gql';

export default {

    /**
     * The shops a given store may join, filtered server-side by the same rule the
     * attach enforces - so the picker can never offer what the mutation refuses.
     */
    fetchAttachableShops: cmsId =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: AttachableShopsForCmsGql,
                    variables: { cms_id: parseInt(cmsId) },
                    fetchPolicy: 'network-only'
                })
                .then(({ data }) => resolve(data.CP_AttachableShops))
                .catch(err => reject(err));
        }),

    /**
     * Multistore: make an existing store a satellite of a shared shop.
     *
     * Super-user only, and the same-owner rule is enforced server-side in
     * ShopProvisioner::attachToShop() - a refusal comes back as a 422 message.
     * The caller re-fetches the CMS afterwards: attaching changes the whole shop
     * half of the settings, not just the fields in this response.
     */
    attachCmsToShop: (cmsId, shopId) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: AttachCmsToShopGql,
                    variables: {
                        cms_id: parseInt(cmsId),
                        shop_id: parseInt(shopId),
                    }
                })
                .then(({ data }) => {
                    resolve(data.CP_AttachCmsToShop);
                })
                .catch(err => reject(err));
        }),
    fetchCms: id =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: CmsGql,
                    variables: { id: parseInt(id) }
                })
                .then(({ data }) => resolve(data.CP_Cms))
                .catch(err => reject(err));
        }),
    fetchCmssPaginated: data =>
        new Promise((resolve, reject) => {
            let filters = [];
            _.each(data.filters, entry => filters.push(entry));

            apolloClient
                .query({
                    query: CmssPaginated,
                    variables: {
                        currentPage: _.get(data, 'pageInfo.currentPage', 1),
                        perPage: _.get(data, 'pageInfo.perPage', 25),
                        order: _.get(data, 'variables.order'),
                        direction: _.get(data, 'variables.direction'),
                        filters
                    }
                })
                .then(({ data }) => resolve(data.CP_CMSsPaginated))
                .catch(err => reject(err));
        }),
    update: cms => {
        if (cms === undefined) throw new Error('required argument is missing');

        cms = _.removeDeepByKey(cms, '__typename');
        // Signatures and Textvorlagen are staged by the communication settings
        // tab, so a row the server has never seen carries a NEGATIVE placeholder
        // id — the table needs a key before the row has a real one. The mutation
        // reads "no id" as "create", so the placeholder is dropped here.
        const stripStagedIds = rows => (rows || []).map(row => (Number(row.id) < 0 ? _.omit(row, 'id') : row));
        if (cms.signatures) cms.signatures = stripStagedIds(cms.signatures);
        if (cms.messagePresets) cms.messagePresets = stripStagedIds(cms.messagePresets);
        delete cms.disabled_at;   // read-only, not in input type
        delete cms.disabled_reason; // read-only, not in input type
        delete cms.is_disabled;   // computed field, not in input type
        delete cms.is_business_card; // computed from billing feature, not in input type
        delete cms.effective_business_card_mode; // computed accessor, not in input type
        delete cms.is_shop_owner;   // computed multistore role flag, not writable
        delete cms.is_shop_satellite; // computed multistore role flag, not writable
        delete cms.can_send_email; // computed from the transactional channel, not writable
        if (cms.shop) delete cms.shop.can_manage_shared_data; // computed per-caller flag, not writable

        // Merge blueprintScreenshots into images (they use same polymorphic relation)
        // First, filter out blueprint-screenshots from images to avoid duplicates
        // (backend returns them in both images and blueprintScreenshots)
        if (cms.images) {
            cms.images = cms.images.filter(img => img.type !== 'blueprint-screenshot');
        }
        if (cms.blueprintScreenshots && cms.blueprintScreenshots.length) {
            if (!cms.images) cms.images = [];
            cms.images = [...cms.images, ...cms.blueprintScreenshots];
        }
        delete cms.blueprintScreenshots;

        // Normalise a BusinessCard input for the mutation — image/meta_tags
        // shape and dropping server-managed read-only fields. Category and
        // address travel as nested ref objects already (no flat _id wart).
        const sanitizeBusinessCardInput = (bc) => {
            if (!bc) return;
            delete bc.id;
            delete bc.__typename;

            if (bc.image) {
                bc.image = {
                    id: bc.image.id,
                    type: bc.image.type,
                    upload: bc.image.upload || false,
                    deleted: bc.image.deleted || false,
                    settings: bc.image.settings || null,
                    title: bc.image.title || null,
                    alt: bc.image.alt || null,
                    width: bc.image.width || null,
                    height: bc.image.height || null,
                    // Keep source so the backend can attach a library selection (new:file-library:).
                    source: bc.image.source ? { id: parseInt(bc.image.source.id, 10) || bc.image.source.id } : null
                };
            }

            if (bc.address) {
                delete bc.address.__typename;
            }

            if (bc.meta_tags) {
                bc.meta_tags = bc.meta_tags.map(function (tag) {
                    return { id: tag.id, key: tag.key, name: tag.name, deleted: tag.deleted };
                });
            }

            delete bc.indexed_at;
            delete bc.index_dirty_since;
        };

        if (cms.businessCard) {
            sanitizeBusinessCardInput(cms.businessCard);
        }

        if (cms.businessCards && cms.businessCards.length) {
            cms.businessCards.forEach(sanitizeBusinessCardInput);
        } else if (cms.businessCards) {
            delete cms.businessCards;
        }

        // Handle config.template - business card CMSs may not have a config
        if (cms.config) {
            const template = _.get(cms, 'config.template.id');
            cms.config.template = template;
        }

        // The mailbox's POS links: the output field is `points_of_sale`; the input
        // accepts only `pointsOfSale: [{ id }]`. Translate and drop the output field.
        if (cms.smtp) {
            const posLinks = cms.smtp.pointsOfSale || cms.smtp.points_of_sale;
            if (posLinks) {
                cms.smtp.pointsOfSale = posLinks.map(pointOfSale => ({ id: pointOfSale.id }));
            }
            delete cms.smtp.points_of_sale;
        }

        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: UpdateCms,
                    variables: { cms }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.CP_UpdateCms);
                })
                .catch(err => reject(err));
        });
    },
    create: cms => {
        if (cms === undefined) throw new Error('required argument is missing');

        cms = _.removeDeepByKey(cms, '__typename');
        const template = _.get(cms, 'config.template.id');
        cms.config.template = template;
        // cms.id = uniqid('new:')
        cms.id = null

        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: CreateCms,
                    variables: { cms }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.CP_CreateCms);
                })
                .catch(err => reject(err));
        });
    },
    // update: cms =>{
    //     user = _.removeDeepByKey(user, '__typename');

    // return new Promise((resolve, reject) => {
    //     apolloClient
    //         .mutate({
    //             mutation: UpdateCms,
    //             variables: { cms }
    //         })
    //         .then(({ data }) => resolve(data.CP_Cms))
    //         .catch(err => reject(err));
    //     }
    // }),
    delete: id => {
        if (id === undefined) throw new Error('required argument is missing');

        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: DeleteCms,
                    variables: { id: parseInt(id) }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.CP_DeleteCms);
                })
                .catch(err => reject(err));
        });
    },
    fetchList: (ownerID = null) =>
        new Promise((resolve, reject) => {
            let variables = {};
            if (ownerID) variables.owner = parseInt(ownerID);

            apolloClient
                .query({
                    query: CMSsGql,
                    variables
                })
                .then(({ data }) => resolve(data.CP_CMSs))
                .catch(err => reject(err));
        }),
    copySettings: (source, target, includeImages = false) => {
        if (source === undefined) throw new Error('required argument is missing');
        if (target === undefined) throw new Error('required argument is missing');

        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: CopyCmsSettingsGql,
                    variables: {
                        source: parseInt(source),
                        target: parseInt(target),
                        includeImages
                    }
                })
                .then(({ data }) => {
                    resolve(data.CP_CopyCmsSettings);
                })
                .catch(err => reject(err));
        });
    },
    processPaginationBulkAction: function (action, payload, data) {
        var filters = [];
        _.each(data.filters, function (entry) { filters.push(entry); });

        return new Promise(function (resolve, reject) {
            apolloClient
                .mutate({
                    mutation: ProcessCmsPaginationBulkActionGql,
                    variables: {
                        action: action,
                        payload: JSON.stringify(payload),
                        filters: filters,
                        selection: {
                            type: _.get(data, 'pageInfo.selection.type', 'inclusive'),
                            identifiers: _.get(data, 'pageInfo.selection.identifiers', []),
                        },
                        order: _.get(data, 'variables.order', 'id'),
                        direction: _.get(data, 'variables.direction', 'asc'),
                    },
                })
                .then(function (response) { resolve(response.data.CP_ProcessCmsPaginationBulkAction); })
                .catch(function (error) { reject(error); });
        });
    },
    clearCache: cms => {
        if (cms === undefined) throw new Error('required argument is missing');

        cms = _.removeDeepByKey(cms, '__typename');
        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: ClearCacheForCmsGql,
                    variables: {
                        cms: { id: parseInt(cms.id) },
                    }
                })
                .then(({ data }) => {
                    resolve(data.CP_ClearCacheForCms);
                })
                .catch(err => reject(err));
        });
    },
};
